import React from "react";

// Shows what a run actually executed with: the LLM, voice and transcription
// providers/models, the persona and the language.
//
// Reads run.metadata.run_config, stamped by the backend at dispatch. It is a
// snapshot on purpose — agent settings are editable and reruns reuse the same
// Run row, so reading them off the agent now would show today's configuration
// rather than the one that produced this transcript.
//
// Runs from before the snapshot existed have no run_config, so the panel hides
// itself entirely rather than rendering a grid of em-dashes.

export const RunConfigRow = ({ label, provider, model, extra }) => {
  if (!provider && !model) return null;
  return (
    <div>
      <span className="text-xs text-muted-foreground block">{label}</span>
      <span className="font-medium capitalize">{provider || "—"}</span>
      {model && (
        <span className="text-xs text-muted-foreground block font-mono break-all">
          {model}
        </span>
      )}
      {extra && (
        <span className="text-xs text-muted-foreground block break-all">{extra}</span>
      )}
    </div>
  );
};

// A single labelled value (used by the turn-taking / VAD sub-grid). Renders 0s
// too — `null`/`undefined` is the only "not set" (callers guard with `!= null`).
const ConfigStat = ({ label, value }) => (
  <div>
    <span className="text-xs text-muted-foreground block">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

// Stress-test runs are stamped with how the caller side was driven: a live
// "intelligent" AI persona. Normal (non-stress) runs carry no caller_type, so
// the badge simply doesn't render.
const CALLER_TYPE_LABELS = {
  intelligent: "Intelligent",
  normal: "Normal",
};

// Same fallback shape as CALLER_TYPE_LABELS: an unknown/future value renders as
// itself rather than being silently miscast as "Off" (a hardcoded 3-way ternary
// here previously showed "Off" for "unified" runs, and would for any mode added
// after it too — see admin/AdminTurnTaking.js's SMART_ENDPOINTING_OPTIONS for
// the source of truth on what modes exist).
const SMART_ENDPOINTING_LABELS = {
  context: "Context-aware",
  unified: "Unified",
  off: "Off",
  // Not user-selectable, but a run's resolved config can still show these:
  // "smart" is what context degrades to on non-English calls; "native" is a
  // removed mode a legacy stored value may still carry.
  smart: "Smart (non-English fallback)",
  native: "Native (removed)",
};

const RunConfigPanel = ({ config }) => {
  if (!config || Object.keys(config).length === 0) return null;

  const { llm, tts, asr, persona, language, caller_type, turn_taking } = config;
  const hasTurnTaking =
    turn_taking ||
    asr?.endpointing != null ||
    asr?.utterance_end_ms != null;
  const callerLabel = caller_type
    ? CALLER_TYPE_LABELS[caller_type] || caller_type
    : null;

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="text-sm font-semibold text-foreground mb-1">
        Configuration Used
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        What this run actually executed with.
      </p>
      {callerLabel && (
        <div className="mb-4">
          <span className="text-xs text-muted-foreground block mb-1">Caller</span>
          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            {callerLabel}
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <RunConfigRow label="LLM" provider={llm?.provider} model={llm?.model} />
        <RunConfigRow
          label="Voice (TTS)"
          provider={tts?.provider}
          model={tts?.model}
          extra={tts?.voice ? `Voice: ${tts.voice}` : null}
        />
        <RunConfigRow
          label="Transcription (ASR)"
          provider={asr?.provider}
          model={asr?.model}
          extra={asr?.language ? `Language: ${asr.language}` : null}
        />
        {persona?.name && (
          <div>
            <span className="text-xs text-muted-foreground block">Persona</span>
            <span className="font-medium">{persona.name}</span>
          </div>
        )}
        {language && (
          <div>
            <span className="text-xs text-muted-foreground block">Language</span>
            <span className="font-medium">{language}</span>
          </div>
        )}
      </div>

      {hasTurnTaking && (
        <div className="mt-4 pt-4 border-t border-border">
          <span className="text-xs text-muted-foreground block mb-2">
            Turn-taking / VAD
          </span>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            {turn_taking?.smart_endpointing && (
              <ConfigStat
                label="Mode"
                value={
                  SMART_ENDPOINTING_LABELS[turn_taking.smart_endpointing] ||
                  turn_taking.smart_endpointing
                }
              />
            )}
            {turn_taking?.end_of_turn_silence_secs != null && (
              <ConfigStat
                label="End-of-turn silence"
                value={`${turn_taking.end_of_turn_silence_secs} s`}
              />
            )}
            {turn_taking?.resume_grace_secs != null && (
              <ConfigStat
                label="Resume grace"
                value={`${turn_taking.resume_grace_secs} s`}
              />
            )}
            {asr?.endpointing != null && (
              <ConfigStat label="Endpointing" value={`${asr.endpointing} ms`} />
            )}
            {asr?.utterance_end_ms != null && (
              <ConfigStat label="Utterance end" value={`${asr.utterance_end_ms} ms`} />
            )}
            {turn_taking?.interruption_min_words != null && (
              <ConfigStat
                label="Interrupt · min words"
                value={turn_taking.interruption_min_words}
              />
            )}
            {turn_taking?.interruption_min_voice_secs != null && (
              <ConfigStat
                label="Interrupt · min voice"
                value={`${turn_taking.interruption_min_voice_secs} s`}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RunConfigPanel;
